import { last } from "lodash-es";
import { defineStore } from "pinia";

import { boardHistoryFilterChanged, boardHistoryTabChanged } from "@/analytics/events/history";
import { trackEvent } from "@/analytics/track";
import type { Card } from "@/model/card";
import type { ChangeTypeItem } from "@/model/change";
import { serverStickyActivityKeys } from "@/model/change";
import type { Iteration } from "@/model/session";
import type { StickyType } from "@/model/stickyType";
import { endOfDay, lastWeekDay, plusDays, startOfDay, startOfWeek } from "@/utils/date";
import { toKebabCase } from "@/utils/text/text";

import { useBoardStore } from "./board";
import { useCardStore } from "./card";
import { resetAllCardMarks } from "./helpers/mark";
import { useSessionStore } from "./session";

export interface DateItem {
  id: string;
  fromDate: Date;
  toDate: Date;
}

export interface DateFilter {
  id: "now" | "workday" | "iteration" | "pi" | "planning_start" | "week" | "24_hours" | "custom";
  name: string;
  fromDate: () => Date;
  toDate: () => Date;
  show: () => boolean;
}

export interface EventFilter {
  property: string;
  values: unknown[];
}

export type BoardHistoryTab = "log" | "compare";

const dateFilterValues: DateFilter[] = [
  {
    id: "24_hours",
    name: /*$t*/ "boardDiff.last24Hours",
    fromDate: () => plusDays(new Date(), -1),
    toDate: () => new Date(),
    show: () => true,
  },
  {
    id: "workday",
    name: /*$t*/ "boardDiff.lastWorkday",
    fromDate: () => startOfDay(lastWeekDay(new Date())),
    toDate: () => new Date(),
    show: () => true,
  },
  {
    id: "week",
    name: /*$t*/ "boardDiff.thisWeek",
    fromDate: () => startOfDay(startOfWeek(new Date())),
    toDate: () => new Date(),
    show: () => ![1, 2].includes(new Date().getDay()), // the filter isn't useful on monday/tuesday
  },
  {
    id: "iteration",
    name: /*$t*/ "boardDiff.thisIteration",
    fromDate: () => useSessionStore().currentIteration!.start,
    toDate: () => endOfDay(plusDays(useSessionStore().currentIteration!.end, -1)),
    show: () => !!useSessionStore().currentIteration, // show during the PI
  },
  {
    id: "pi",
    name: /*$t*/ "boardDiff.thisPI",
    fromDate: () => useSessionStore().iterations[0].start,
    toDate: () => endOfDay(plusDays(last(useSessionStore().iterations)!.end, -1)),
    show: () => useSessionStore().sessionHasStarted, // show during the PI
  },
  {
    id: "planning_start",
    name: /*$t*/ "boardDiff.planningDates",
    fromDate: () => startOfDay(useSessionStore().current.planningStart!),
    toDate: () => endOfDay(useSessionStore().current.planningEnd!),
    show: () =>
      !useSessionStore().sessionHasStarted && // show during planning, if dates provided
      !!useSessionStore().current.planningStart &&
      !!useSessionStore().current.planningEnd,
  },
];

const defaultDate: DateItem = {
  id: dateFilterValues[0].id,
  fromDate: dateFilterValues[0].fromDate(),
  toDate: dateFilterValues[0].toDate(),
};

interface State {
  card: Card | null;
  board: boolean;
  tab: BoardHistoryTab;
  selectedIterationIds: Set<number>;
  selectedStickyTypeIds: Set<string>;
  selectedDate: DateItem;
  selectedChangeTypes: ChangeTypeItem[];
}

const defaultTab = "log";

export const useActivityStore = defineStore("activity", {
  state: (): State => ({
    card: null as Card | null,
    board: false,
    tab: defaultTab,
    selectedIterationIds: new Set(),
    selectedStickyTypeIds: new Set(),
    selectedDate: defaultDate,
    selectedChangeTypes: [],
  }),
  getters: {
    // Active State
    active: (state) => !!state.card || !!state.board,
    isBoardActivityOpen: (state) => state.board,
    isCardActivityOpen: (state) => !!state.card,
    // Filters
    anyFilterCriteria: (state): boolean =>
      state.selectedStickyTypeIds.size !== 0 || state.selectedIterationIds.size !== 0,
    allFilters(state) {
      // Fetch the server-version of change types, and add the unlink/unmirror
      // types if the reciprocal is already in the list
      // (for the user, this means that selecting 'link' changes will also show unlink changes)
      const changeTypes = state.selectedChangeTypes.map((t) => serverStickyActivityKeys[t.id]);
      if (changeTypes.includes("link")) {
        changeTypes.push("unlink");
      }
      if (changeTypes.includes("mirror")) {
        changeTypes.push("unmirror");
      }

      return {
        board: state.board,
        date: state.selectedDate,
        iterations: [...state.selectedIterationIds],
        types: [...state.selectedStickyTypeIds],
        changeTypes,
      };
    },
    serverFilters: (state): EventFilter[] => {
      const filters = [];
      if (state.selectedIterationIds.size) {
        filters.push({ property: "iteration_number", values: [...state.selectedIterationIds] });
      }
      if (state.selectedStickyTypeIds.size) {
        filters.push({ property: "type_id", values: [...state.selectedStickyTypeIds] });
      }

      return filters;
    },
    // Date Filter
    dateFilterOptions: (): DateFilter[] => dateFilterValues.filter((i) => i.show()),
    // Iterations
    allIterations: (): Iteration[] => useSessionStore().iterations,
    selectedIterations(state): Iteration[] {
      return this.allIterations.filter((iteration) => state.selectedIterationIds.has(iteration.id));
    },
    // Sticky Types
    selectedStickyTypes: (state): StickyType[] =>
      useBoardStore().possibleStickyTypes.filter((type) =>
        state.selectedStickyTypeIds.has(type.id),
      ),
  },
  actions: {
    // Active State
    /**
     * Toggles the card-history panel
     */
    toggleCard(id: string) {
      if (this.card?.id === id) return this.inactivate();

      this.card = useCardStore().cards[id];
      // Close board history if it's open
      this.inactivateBoardHistory();
    },
    /**
     * Toggles the board-history panel
     */
    toggleBoard() {
      if (this.board) return this.inactivate();

      this.board = true;
      // Close card history if it's open
      this.inactivateCardHistory();
    },
    inactivate() {
      this.inactivateCardHistory();
      this.inactivateBoardHistory();
    },
    inactivateBoardHistory() {
      if (!this.board) return;

      this.board = false;
      this.resetFilters();
      this.resetTab();
    },
    inactivateCardHistory() {
      if (!this.card) return;

      this.card = null;
    },
    toggleTab() {
      this.tab = this.tab === "log" ? "compare" : "log";
      trackEvent(boardHistoryTabChanged(this.tab));
    },
    resetTab() {
      this.tab = defaultTab;
    },
    // Filters
    resetFilters() {
      this.selectedIterationIds = new Set();
      this.selectedStickyTypeIds = new Set();
      this.selectedDate = defaultDate;
      this.selectedChangeTypes = [];
      resetAllCardMarks(useBoardStore().currentBoard());
    },
    selectIterations(iterations: Iteration[]) {
      this.selectedIterationIds = new Set(iterations.map((i) => i.id));
      trackEvent(
        boardHistoryFilterChanged({
          filterName: "iteration",
          selectedValues: iterations.map((i) => i.id.toString()),
          tab: this.tab,
        }),
      );
    },
    selectStickyTypes(stickyTypes: StickyType[]) {
      this.selectedStickyTypeIds = new Set(stickyTypes.map((t) => t.id));
      trackEvent(
        boardHistoryFilterChanged({
          filterName: "sticky-type",
          selectedValues: stickyTypes.map((i) => i.id),
          tab: this.tab,
        }),
      );
    },
    selectChangeTypes(changeTypes: ChangeTypeItem[]) {
      this.selectedChangeTypes = changeTypes;
      trackEvent(
        boardHistoryFilterChanged({
          filterName: "change-type",
          selectedValues: changeTypes.map((i) => toKebabCase(i.id)),
          tab: this.tab,
        }),
      );
    },
  },
});
